/* eslint-disable max-len */
import * as React from 'react';
import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';

import { cn } from '../lib/utils';

const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-semibold',
    'transition-all disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none',
    "[&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0 outline-none cursor-pointer",
    'has-[>svg]:px-3 focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
    'focus-visible:ring-offset-2 focus-visible:ring-offset-background aria-invalid:ring-destructive/20',
    'dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
  ].join(' '),
  {
    variants: {
      variant: {
        // Primary teal button (like your "Next" button)
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',

        // Red destructive button (like your "Delete" button)
        destructive: [
          'bg-destructive text-white hover:bg-destructive/90 focus-visible:ring-destructive/20',
          'dark:focus-visible:ring-destructive/40 dark:bg-destructive/60',
        ].join(' '),

        // Outline button (like your "Back" button)
        outline: [
          'border bg-background hover:bg-accent hover:text-accent-foreground',
          'dark:bg-input/30 dark:border-input dark:hover:bg-input/50',
        ].join(' '),

        // Light teal secondary button (like your "Cancel" button)
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',

        // Light gray ghost button (like your "Dismiss" button)
        ghost: 'hover:bg-accent hover:text-accent-foreground dark:hover:bg-accent/50',

        // Link style button
        link: 'text-primary underline-offset-4 hover:underline',
      },
      size: {
        default: 'h-9 px-4 py-2 has-[>svg]:px-3',
        sm: 'h-8 rounded-md gap-1.5 px-3 has-[>svg]:px-2.5',
        lg: 'h-10 rounded-md px-6 has-[>svg]:px-4',
        icon: 'size-9',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

type ButtonProps = React.ComponentPropsWithoutRef<'button'> &
VariantProps<typeof buttonVariants> & {
  asChild?: boolean;
};

const Button = React.forwardRef<React.ElementRef<'button'>, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';

    return (
      <Comp
        ref={ref}
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
        data-oid="jyt6oqu"
      />
    );
  }
);
Button.displayName = 'Button';

export { Button, buttonVariants };
